import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ArrowLeft, Save, X, Check } from "lucide-react";
import { PACKAGE_SCOPES } from "../../components/portfolio/scopes";

export default function PortfolioAdd() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    apiClient.get("/portfolio/categories").then(res => setCategories(res.data)).catch(() => {});
  }, []);

  const [form, setForm] = useState({
    title: "",
    location: "",
    client_name: "",
    project_type: "",
    delivered_on: "",
    short_description: "",
    status: "Draft"
  });
  // The package sold on this project — ticked here, it gives the project page
  // its shape straight away, so every department knows what's expected of it.
  const [includedScopes, setIncludedScopes] = useState([]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title.trim()) {
      toast.error("Project title is required");
      return;
    }
    
    setSaving(true);
    try {
      const response = await apiClient.post("/portfolio/projects", {
        ...form,
        tags,
        included_scopes: includedScopes,
      });

      toast.success("Project created");
      navigate(`/portfolio/${response.data.id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="portfolio-add">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/portfolio")} className="rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'var(--font-sans)' }}>
            Add a project
          </h1>
          <p className="text-muted-foreground text-sm">
            The basics now — every department fills in its own scope next
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Project Details</CardTitle>
          <CardDescription>
            Basic information about this portfolio project
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Project Title *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="e.g., The Azure Residences"
                data-testid="title-input"
              />
            </div>

            {/* Client + location */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client_name">Client</Label>
                <Input
                  id="client_name"
                  value={form.client_name}
                  onChange={(e) => handleChange("client_name", e.target.value)}
                  placeholder="e.g., Terra Group"
                  data-testid="client-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={form.location}
                  onChange={(e) => handleChange("location", e.target.value)}
                  placeholder="e.g., Miami, Florida"
                  data-testid="location-input"
                />
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="project_type">Category</Label>
              <Select value={form.project_type} onValueChange={(v) => handleChange("project_type", v)}>
                <SelectTrigger data-testid="type-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Delivered */}
            <div className="space-y-2">
              <Label htmlFor="delivered_on">Delivered</Label>
              <Input
                id="delivered_on"
                value={form.delivered_on}
                onChange={(e) => handleChange("delivered_on", e.target.value)}
                placeholder="e.g., 2026 or 2026-04-18"
                data-testid="delivered-input"
              />
            </div>

            {/* The package — what we sold on this project */}
            <div className="space-y-2">
              <Label>What did we sell on this project?</Label>
              <p className="text-xs text-muted-foreground">
                Each one becomes a section every department fills in. You can change this later.
              </p>
              <div className="grid grid-cols-2 gap-2 pt-1">
                {PACKAGE_SCOPES.map(scope => {
                  const on = includedScopes.includes(scope.key);
                  return (
                    <button
                      key={scope.key}
                      type="button"
                      onClick={() => setIncludedScopes(prev =>
                        on ? prev.filter(k => k !== scope.key) : [...prev, scope.key]
                      )}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-smooth ${
                        on ? "border-primary bg-secondary/60" : "border-border hover:bg-secondary/40"
                      }`}
                      data-testid={`scope-toggle-${scope.key}`}
                    >
                      <scope.Icon className={`w-4 h-4 shrink-0 ${on ? "text-foreground" : "text-muted-foreground"}`} />
                      <span className="text-sm">{scope.short}</span>
                      {on && <Check className="w-3.5 h-3.5 ml-auto text-foreground" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Short Description</Label>
              <Textarea
                id="description"
                value={form.short_description}
                onChange={(e) => handleChange("short_description", e.target.value)}
                placeholder="A brief description of the project..."
                rows={3}
                data-testid="description-input"
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  data-testid="tag-input"
                />
                <Button type="button" variant="outline" onClick={addTag}>
                  Add
                </Button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {tags.map(tag => (
                    <span 
                      key={tag} 
                      className="inline-flex items-center gap-1 px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-sm"
                    >
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={form.status} onValueChange={(v) => handleChange("status", v)}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Published">Published</SelectItem>
                  <SelectItem value="Archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Only published projects are visible to non-admins
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate("/portfolio")} className="rounded-full">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="rounded-full" data-testid="save-btn">
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create & Add Images
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
